import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import { ArrowLeft, Coffee, Edit, Plus, Printer, Save, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const FoodUnitForm = ({ currentExercise, theme }) => {
  const API = API_BASE_URL + '/api';
  const [view, setView] = useState('list');
  const [entries, setEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    exercise_name: currentExercise?.exercise_name || '',
    incident_name: '',
    operational_period_from: '',
    operational_period_to: '',
    food_unit_leader: '',
    duration_estimate: '',
    last_meal_provided: '',
    // Personnel counts
    total_personnel: '',
    incoming_resources: '',
    outgoing_resources: '',
    expected_next_period: '',
    // Personnel by location
    personnel_locations: [],
    // Menu planning
    meal_schedules: [],
    special_dietary_requirements: '',
    // Food orders
    food_orders: [],
    // Meals served log
    meals_served: [],
    // Water & beverages (now dynamic)
    water_beverage_records: [],
    // Facility information (now dynamic)
    facility_records: [],
    // Cost tracking
    total_estimated_cost: '',
    budget_status: '',
    // Prepared by
    prepared_by: '',
    signature: '',
    date_time_prepared: ''
  });
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [lastSynced, setLastSynced] = useState(null);
  const [cardSaveStatus, setCardSaveStatus] = useState({}); // Track save status per card

  // Save a specific card's data
  const saveCard = async (cardName) => {
    setCardSaveStatus(prev => ({ ...prev, [cardName]: 'saving' }));
    try {
      await saveEntry();
      setCardSaveStatus(prev => ({ ...prev, [cardName]: 'saved' }));
      setTimeout(() => {
        setCardSaveStatus(prev => ({ ...prev, [cardName]: null }));
      }, 3000);
    } catch (error) {
      setCardSaveStatus(prev => ({ ...prev, [cardName]: 'error' }));
      setTimeout(() => {
        setCardSaveStatus(prev => ({ ...prev, [cardName]: null }));
      }, 5000);
    }
  };

  // Render save status indicator
  const renderSaveStatus = (cardName) => {
    const status = cardSaveStatus[cardName];
    if (status === 'saving') return <span className="text-yellow-500 text-sm ml-2">Saving...</span>;
    if (status === 'saved') return <span className="text-green-500 text-sm ml-2">✓ Saved</span>;
    if (status === 'error') return <span className="text-red-500 text-sm ml-2">Save failed</span>;
    return null;
  };

  // Fetch map markers as locations for dropdown - filtered by current exercise
  const fetchMapMarkers = async () => {
    try {
      const exerciseId = currentExercise?.id;
      const url = exerciseId 
        ? `${API}/map-objects?type=marker&exercise_id=${exerciseId}`
        : `${API}/map-objects?type=marker`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setLocations(data.map(marker => ({ id: marker.id, name: marker.name })));
      }
    } catch (error) {
      console.error('Error fetching map markers:', error);
    }
  };

  // Fetch all Food Unit entries for current exercise
  const fetchEntries = async (silent = false) => {
    if (!currentExercise?.id) return;
    
    if (!silent) setLoading(true);
    try {
      const response = await fetch(`${API}/food-unit?exercise_id=${currentExercise.id}`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
        setLastSynced(new Date());
      }
    } catch (error) {
      if (!silent) console.error('Error fetching Food Unit entries:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
    fetchMapMarkers();
  }, [currentExercise?.id]);

  // Setup polling for real-time sync (every 30 seconds)
  useEffect(() => {
    if (currentExercise?.id) {
      const pollInterval = setInterval(() => {
        fetchEntries(true);
      }, 30000);
      
      return () => clearInterval(pollInterval);
    }
  }, [currentExercise?.id]);

  // Initialize form with editing data
  useEffect(() => {
    if (editingEntry) {
      setFormData({
        exercise_name: editingEntry.exercise_name || currentExercise?.exercise_name || '',
        incident_name: editingEntry.incident_name || '',
        operational_period_from: editingEntry.operational_period_from || '',
        operational_period_to: editingEntry.operational_period_to || '',
        food_unit_leader: editingEntry.food_unit_leader || '',
        duration_estimate: editingEntry.duration_estimate || '',
        last_meal_provided: editingEntry.last_meal_provided || '',
        total_personnel: editingEntry.total_personnel || '',
        incoming_resources: editingEntry.incoming_resources || '',
        outgoing_resources: editingEntry.outgoing_resources || '',
        expected_next_period: editingEntry.expected_next_period || '',
        personnel_locations: editingEntry.personnel_locations || [],
        meal_schedules: editingEntry.meal_schedules || [],
        special_dietary_requirements: editingEntry.special_dietary_requirements || '',
        food_orders: editingEntry.food_orders || [],
        meals_served: editingEntry.meals_served || [],
        water_beverage_records: editingEntry.water_beverage_records || [],
        facility_records: editingEntry.facility_records || [],
        total_estimated_cost: editingEntry.total_estimated_cost || '',
        budget_status: editingEntry.budget_status || '',
        prepared_by: editingEntry.prepared_by || '',
        signature: editingEntry.signature || '',
        date_time_prepared: editingEntry.date_time_prepared || ''
      });
    } else {
      setFormData({
        exercise_name: currentExercise?.exercise_name || '',
        incident_name: '',
        operational_period_from: '',
        operational_period_to: '',
        food_unit_leader: '',
        duration_estimate: '',
        last_meal_provided: '',
        total_personnel: '',
        incoming_resources: '',
        outgoing_resources: '',
        expected_next_period: '',
        personnel_locations: [],
        meal_schedules: [],
        special_dietary_requirements: '',
        food_orders: [],
        meals_served: [],
        water_beverage_records: [],
        facility_records: [],
        total_estimated_cost: '',
        budget_status: '',
        prepared_by: '',
        signature: '',
        date_time_prepared: ''
      });
    }
  }, [editingEntry, currentExercise?.id, currentExercise?.exercise_name]);

  // Personnel Location functions
  const addPersonnelLocation = () => {
    const newLocation = {
      id: Date.now().toString(),
      location: '',
      assignment: '',
      count: ''
    };
    setFormData(prev => ({
      ...prev,
      personnel_locations: [newLocation, ...prev.personnel_locations]
    }));
  };

  const updatePersonnelLocation = (index, field, value) => {
    const updated = [...formData.personnel_locations];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, personnel_locations: updated }));
  };

  const deletePersonnelLocation = (index) => {
    setFormData(prev => ({
      ...prev,
      personnel_locations: prev.personnel_locations.filter((_, i) => i !== index)
    }));
  };

  // Meal Schedule functions
  const addMealSchedule = () => {
    const newSchedule = {
      id: Date.now().toString(),
      meal_type: '',
      scheduled_time: '',
      menu: '',
      estimated_count: ''
    };
    setFormData(prev => ({
      ...prev,
      meal_schedules: [newSchedule, ...prev.meal_schedules]
    }));
  };

  const updateMealSchedule = (index, field, value) => {
    const updated = [...formData.meal_schedules];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, meal_schedules: updated }));
  };

  const deleteMealSchedule = (index) => {
    setFormData(prev => ({
      ...prev,
      meal_schedules: prev.meal_schedules.filter((_, i) => i !== index)
    }));
  };

  // Food Order functions
  const addFoodOrder = () => {
    const newOrder = {
      id: Date.now().toString(),
      vendor_name: '',
      contact_info: '',
      items_ordered: '',
      quantity: '',
      cost: '',
      delivery_time: '',
      order_date: ''
    };
    setFormData(prev => ({
      ...prev,
      food_orders: [newOrder, ...prev.food_orders]
    }));
  };

  const updateFoodOrder = (index, field, value) => {
    const updated = [...formData.food_orders];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, food_orders: updated }));
  };

  const deleteFoodOrder = (index) => {
    setFormData(prev => ({
      ...prev,
      food_orders: prev.food_orders.filter((_, i) => i !== index)
    }));
  };

  // Meals Served functions
  const addMealServed = () => {
    const newMeal = {
      id: Date.now().toString(),
      meal_type: '',
      date_time_served: '',
      meals_count: '',
      location: '',
      notes: ''
    };
    setFormData(prev => ({
      ...prev,
      meals_served: [newMeal, ...prev.meals_served]
    }));
  };

  const updateMealServed = (index, field, value) => {
    const updated = [...formData.meals_served];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, meals_served: updated }));
  };

  const deleteMealServed = (index) => {
    setFormData(prev => ({
      ...prev,
      meals_served: prev.meals_served.filter((_, i) => i !== index)
    }));
  };

  // Water & Beverage functions
  const addWaterBeverageRecord = () => {
    const newRecord = {
      id: Date.now().toString(),
      water_quantity: '',
      beverage_type: '',
      quantity: '',
      cost: '',
      distribution_point: '',
      delivery_date: '',
      notes: ''
    };
    setFormData(prev => ({
      ...prev,
      water_beverage_records: [newRecord, ...prev.water_beverage_records]
    }));
  };

  const updateWaterBeverageRecord = (index, field, value) => {
    const updated = [...formData.water_beverage_records];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, water_beverage_records: updated }));
  };

  const deleteWaterBeverageRecord = (index) => {
    setFormData(prev => ({
      ...prev,
      water_beverage_records: prev.water_beverage_records.filter((_, i) => i !== index)
    }));
  };

  // Facility Record functions
  const addFacilityRecord = () => {
    const newRecord = {
      id: Date.now().toString(),
      facility_name: '',
      location: '',
      meal_prep_area: '',
      equipment_available: '',
      inspection_date: '',
      inspector_name: '',
      health_safety_notes: '',
      status: ''
    };
    setFormData(prev => ({
      ...prev,
      facility_records: [newRecord, ...prev.facility_records]
    }));
  };

  const updateFacilityRecord = (index, field, value) => {
    const updated = [...formData.facility_records];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, facility_records: updated }));
  };

  const deleteFacilityRecord = (index) => {
    setFormData(prev => ({
      ...prev,
      facility_records: prev.facility_records.filter((_, i) => i !== index)
    }));
  };

  // Calculate total estimated cost
  const calculateTotalCost = () => {
    let total = 0;
    
    // Add costs from food orders
    formData.food_orders.forEach(order => {
      const cost = parseFloat(order.cost?.replace(/[^0-9.-]/g, '') || 0);
      if (!isNaN(cost)) {
        total += cost;
      }
    });
    
    // Add costs from water/beverage records
    formData.water_beverage_records.forEach(record => {
      const cost = parseFloat(record.cost?.replace(/[^0-9.-]/g, '') || 0);
      if (!isNaN(cost)) {
        total += cost;
      }
    });
    
    return total.toFixed(2);
  };

  // Update total cost whenever food orders or water/beverage records change
  useEffect(() => {
    const totalCost = calculateTotalCost();
    setFormData(prev => ({ ...prev, total_estimated_cost: `$${totalCost}` }));
  }, [formData.food_orders, formData.water_beverage_records]);

  // Save Food Unit entry
  const saveEntry = async () => {
    if (!currentExercise?.id) {
      alert('No exercise selected');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        exercise_id: currentExercise.id,
        exercise_name: currentExercise.exercise_name
      };

      let response;
      if (editingEntry) {
        response = await fetch(`${API}/food-unit/${editingEntry.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${API}/food-unit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        alert(editingEntry ? 'Food Unit entry updated successfully!' : 'Food Unit entry created successfully!');
        fetchEntries();
        setView('list');
        setEditingEntry(null);
      } else {
        alert('Failed to save Food Unit entry');
      }
    } catch (error) {
      console.error('Error saving Food Unit entry:', error);
      alert('Error saving Food Unit entry');
    } finally {
      setLoading(false);
    }
  };

  // Delete Food Unit entry
  const deleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this Food Unit entry?')) return;

    setLoading(true);
    try {
      const response = await fetch(`${API}/food-unit/${entryId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Food Unit entry deleted successfully!');
        fetchEntries();
      } else {
        alert('Failed to delete Food Unit entry');
      }
    } catch (error) {
      console.error('Error deleting Food Unit entry:', error);
      alert('Error deleting Food Unit entry');
    } finally {
      setLoading(false);
    }
  };

  // Enhanced print function for individual entries
  const handlePrintEntry = (entry) => {
    const printWindow = window.open('', '_blank');
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Food Unit Report - ${entry.incident_name || 'N/A'}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; }
            h1 { color: #f97316; text-align: center; border-bottom: 3px solid #f97316; padding-bottom: 10px; }
            h2 { color: #f97316; margin-top: 30px; border-bottom: 2px solid #e5e7eb; padding-bottom: 5px; }
            .section { margin-bottom: 30px; }
            .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; margin: 15px 0; }
            .info-item { padding: 10px; background: #f9fafb; border-left: 3px solid #f97316; }
            .info-label { font-weight: bold; color: #6b7280; font-size: 12px; }
            .info-value { color: #111827; margin-top: 3px; }
            table { width: 100%; border-collapse: collapse; margin: 15px 0; }
            th, td { border: 1px solid #e5e7eb; padding: 10px; text-align: left; }
            th { background: #f3f4f6; font-weight: bold; color: #374151; }
            tr:nth-child(even) { background: #f9fafb; }
            .record-card { border: 1px solid #e5e7eb; padding: 15px; margin: 10px 0; background: #f9fafb; border-radius: 5px; }
            .cost-highlight { font-size: 24px; font-weight: bold; color: #10b981; text-align: center; padding: 20px; background: #f0fdf4; border: 2px solid #10b981; border-radius: 8px; margin: 20px 0; }
          </style>
        </head>
        <body>
          <h1>Food Unit Report</h1>
          <p style="text-align: center; color: #6b7280; margin-bottom: 30px;">Generated on ${new Date().toLocaleString()}</p>
          
          <div class="section">
            <h2>Basic Information</h2>
            <div class="info-grid">
              <div class="info-item"><div class="info-label">Exercise Name</div><div class="info-value">${entry.exercise_name || 'N/A'}</div></div>
              <div class="info-item"><div class="info-label">Incident Name</div><div class="info-value">${entry.incident_name || 'N/A'}</div></div>
              <div class="info-item"><div class="info-label">Food Unit Leader</div><div class="info-value">${entry.food_unit_leader || 'N/A'}</div></div>
              <div class="info-item"><div class="info-label">Duration Estimate</div><div class="info-value">${entry.duration_estimate || 'N/A'}</div></div>
              <div class="info-item"><div class="info-label">Operational Period From</div><div class="info-value">${entry.operational_period_from || 'N/A'}</div></div>
              <div class="info-item"><div class="info-label">Operational Period To</div><div class="info-value">${entry.operational_period_to || 'N/A'}</div></div>
            </div>
          </div>
          
          <div class="section">
            <h2>Personnel Information</h2>
            <div class="info-grid">
              <div class="info-item"><div class="info-label">Total Personnel</div><div class="info-value">${entry.total_personnel || '0'}</div></div>
              <div class="info-item"><div class="info-label">Incoming Resources</div><div class="info-value">${entry.incoming_resources || '0'}</div></div>
              <div class="info-item"><div class="info-label">Outgoing Resources</div><div class="info-value">${entry.outgoing_resources || '0'}</div></div>
              <div class="info-item"><div class="info-label">Expected Next Period</div><div class="info-value">${entry.expected_next_period || '0'}</div></div>
            </div>
          </div>
          
          <div class="section">
            <h2>Cost Tracking</h2>
            <div class="cost-highlight">Total Estimated Cost: ${entry.total_estimated_cost || '$0.00'}</div>
            <div class="info-item">
              <div class="info-label">Budget Status</div>
              <div class="info-value">${entry.budget_status || 'N/A'}</div>
            </div>
          </div>
          
          <div class="section">
            <h2>Prepared By</h2>
            <div class="info-grid">
              <div class="info-item"><div class="info-label">Prepared By</div><div class="info-value">${entry.prepared_by || 'N/A'}</div></div>
              <div class="info-item"><div class="info-label">Signature</div><div class="info-value">${entry.signature || 'N/A'}</div></div>
              <div class="info-item"><div class="info-label">Date/Time Prepared</div><div class="info-value">${entry.date_time_prepared || 'N/A'}</div></div>
            </div>
          </div>
          
          <div style="text-align: center; color: #6b7280; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
            <p>Food Unit Report - ${entry.exercise_name || 'EXRSIM'}</p>
            <p style="font-size: 12px;">This is an automatically generated report</p>
          </div>
        </body>
      </html>
    `;
    
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.onload = () => {
      printWindow.print();
    };
  };
  // Simple print function for form view
  // Build a comprehensive Food Unit print report from current formData (all cards, all entries)
  const handlePrint = () => {
    const data = formData || {};
    const exerciseName = data.exercise_name || currentExercise?.exercise_name || 'N/A';
    const incidentName = data.incident_name || 'N/A';
    const generated = new Date().toLocaleString();

    const tableSection = (title, columns, rows, emptyMsg) => {
      if (!rows || rows.length === 0) {
        return `<div class="section"><h2>${title}</h2><p class="empty">${emptyMsg}</p></div>`;
      }
      const head = columns.map(c => `<th>${c.label}</th>`).join('');
      const body = rows.map(r =>
        `<tr>${columns.map(c => `<td>${r[c.key] ?? ''}</td>`).join('')}</tr>`
      ).join('');
      return `
        <div class="section">
          <h2>${title} <span class="count">(${rows.length})</span></h2>
          <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
        </div>`;
    };

    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Food Unit Report - ${exerciseName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.5; color: #111827; }
            h1 { color: #f97316; text-align: center; border-bottom: 3px solid #f97316; padding-bottom: 10px; margin-bottom: 6px; }
            .sub { text-align: center; color: #6b7280; margin-bottom: 24px; font-size: 12px; }
            h2 { color: #f97316; margin-top: 26px; border-bottom: 2px solid #e5e7eb; padding-bottom: 5px; font-size: 16px; }
            .count { color: #6b7280; font-size: 13px; font-weight: normal; }
            .section { margin-bottom: 22px; page-break-inside: avoid; }
            .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin: 10px 0; }
            .info-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 10px 0; }
            .info-item { padding: 8px 10px; background: #f9fafb; border-left: 3px solid #f97316; }
            .info-label { font-weight: bold; color: #6b7280; font-size: 11px; text-transform: uppercase; }
            .info-value { color: #111827; margin-top: 3px; font-size: 13px; }
            table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 12px; }
            th, td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; vertical-align: top; }
            th { background: #fff7ed; font-weight: bold; color: #9a3412; }
            tr:nth-child(even) { background: #f9fafb; }
            .empty { color: #9ca3af; font-style: italic; font-size: 12px; }
            .cost-highlight { font-size: 20px; font-weight: bold; color: #10b981; text-align: center; padding: 14px; background: #f0fdf4; border: 2px solid #10b981; border-radius: 6px; margin: 10px 0; }
            .footer { text-align: center; color: #6b7280; margin-top: 28px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 11px; }
            @media print { body { padding: 12px; } .section { page-break-inside: avoid; } }
          </style>
        </head>
        <body>
          <h1>Food Unit Report</h1>
          <div class="sub"><strong>Exercise:</strong> ${exerciseName} &nbsp;•&nbsp; <strong>Incident:</strong> ${incidentName} &nbsp;•&nbsp; <strong>Generated:</strong> ${generated}</div>

          <div class="section">
            <h2>Basic Information</h2>
            <div class="info-grid">
              <div class="info-item"><div class="info-label">Exercise Name</div><div class="info-value">${exerciseName}</div></div>
              <div class="info-item"><div class="info-label">Incident Name</div><div class="info-value">${incidentName}</div></div>
              <div class="info-item"><div class="info-label">Operational Period From</div><div class="info-value">${data.operational_period_from || 'N/A'}</div></div>
              <div class="info-item"><div class="info-label">Operational Period To</div><div class="info-value">${data.operational_period_to || 'N/A'}</div></div>
              <div class="info-item"><div class="info-label">Food Unit Leader</div><div class="info-value">${data.food_unit_leader || 'N/A'}</div></div>
              <div class="info-item"><div class="info-label">Duration Estimate</div><div class="info-value">${data.duration_estimate || 'N/A'}</div></div>
              <div class="info-item"><div class="info-label">Last Meal Provided</div><div class="info-value">${data.last_meal_provided || 'N/A'}</div></div>
            </div>
          </div>

          <div class="section">
            <h2>Personnel Information</h2>
            <div class="info-grid">
              <div class="info-item"><div class="info-label">Total Personnel</div><div class="info-value">${data.total_personnel || '0'}</div></div>
              <div class="info-item"><div class="info-label">Incoming Resources</div><div class="info-value">${data.incoming_resources || '0'}</div></div>
              <div class="info-item"><div class="info-label">Outgoing Resources</div><div class="info-value">${data.outgoing_resources || '0'}</div></div>
              <div class="info-item"><div class="info-label">Expected Next Period</div><div class="info-value">${data.expected_next_period || '0'}</div></div>
            </div>
          </div>

          ${tableSection('Personnel by Location',
            [{ key: 'location', label: 'Location' }, { key: 'assignment', label: 'Assignment' }, { key: 'count', label: 'Count' }],
            data.personnel_locations,
            'No personnel location entries.')}

          <div class="section">
            <h2>Meal Planning &amp; Schedules</h2>
            <div class="info-item"><div class="info-label">Special Dietary Requirements</div><div class="info-value">${data.special_dietary_requirements || 'N/A'}</div></div>
          </div>
          ${tableSection('Meal Schedules',
            [{ key: 'meal_type', label: 'Meal Type' }, { key: 'scheduled_time', label: 'Scheduled Time' }, { key: 'menu', label: 'Menu' }, { key: 'estimated_count', label: 'Est. Count' }],
            data.meal_schedules,
            'No meal schedules.')}

          ${tableSection('Food Orders & Procurement',
            [
              { key: 'vendor_name', label: 'Vendor' },
              { key: 'contact_info', label: 'Contact' },
              { key: 'items_ordered', label: 'Items' },
              { key: 'quantity', label: 'Qty' },
              { key: 'cost', label: 'Cost' },
              { key: 'order_date', label: 'Order Date' },
              { key: 'delivery_time', label: 'Delivery Time' }
            ],
            data.food_orders,
            'No food orders.')}

          ${tableSection('Meals Served Log',
            [
              { key: 'meal_type', label: 'Meal Type' },
              { key: 'date_time_served', label: 'Date/Time' },
              { key: 'meals_count', label: 'Meals Count' },
              { key: 'location', label: 'Location' },
              { key: 'notes', label: 'Notes' }
            ],
            data.meals_served,
            'No meals served records.')}

          ${tableSection('Water & Beverages',
            [
              { key: 'water_quantity', label: 'Water Qty' },
              { key: 'beverage_type', label: 'Beverage Type' },
              { key: 'quantity', label: 'Qty' },
              { key: 'cost', label: 'Cost' },
              { key: 'distribution_point', label: 'Distribution Point' },
              { key: 'delivery_date', label: 'Delivery Date' },
              { key: 'notes', label: 'Notes' }
            ],
            data.water_beverage_records,
            'No water/beverage records.')}

          ${tableSection('Facility & Safety Information',
            [
              { key: 'facility_name', label: 'Facility' },
              { key: 'location', label: 'Location' },
              { key: 'meal_prep_area', label: 'Prep Area' },
              { key: 'equipment_available', label: 'Equipment' },
              { key: 'inspection_date', label: 'Inspection Date' },
              { key: 'inspector_name', label: 'Inspector' },
              { key: 'health_safety_notes', label: 'Notes' },
              { key: 'status', label: 'Status' }
            ],
            data.facility_records,
            'No facility records.')}

          <div class="section">
            <h2>Cost Tracking</h2>
            <div class="cost-highlight">Total Estimated Cost: ${data.total_estimated_cost || '$0.00'}</div>
            <div class="info-item"><div class="info-label">Budget Status</div><div class="info-value">${data.budget_status || 'N/A'}</div></div>
          </div>

          <div class="section">
            <h2>Prepared By</h2>
            <div class="info-grid-3">
              <div class="info-item"><div class="info-label">Prepared By</div><div class="info-value">${data.prepared_by || 'N/A'}</div></div>
              <div class="info-item"><div class="info-label">Signature</div><div class="info-value">${data.signature || 'N/A'}</div></div>
              <div class="info-item"><div class="info-label">Date/Time Prepared</div><div class="info-value">${data.date_time_prepared || 'N/A'}</div></div>
            </div>
          </div>

          <div class="footer">
            <div><strong>Food Unit Report</strong> &nbsp;|&nbsp; ${exerciseName}</div>
            <div>Powered by EXRSIM</div>
          </div>
        </body>
      </html>
    `;

    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  // List view
  if (view === 'list') {
    return (
      <div className="p-6">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>Food Unit</h1>
            <p className={`${theme.colors.textMuted}`}>Manage food service operations and feeding coordination for incident personnel</p>
          </div>
          <Button onClick={() => setView('form')} className="bg-orange-500 hover:bg-orange-600">
            <Plus className="h-4 w-4 mr-2" />
            New Entry
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className={`${theme.colors.textMuted}`}>Loading...</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <Coffee className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
            <p className={`${theme.colors.textMuted} mb-4`}>No Food Unit entries found</p>
            <Button onClick={() => setView('form')} className="bg-orange-500 hover:bg-orange-600">
              Create First Entry
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {entries.map((entry) => (
              <Card key={entry.id} className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className={`text-xl font-semibold ${theme.colors.textPrimary} mb-2`}>
                        {entry.incident_name || 'Food Unit Entry'}
                      </h3>
                      <div className={`text-sm ${theme.colors.textMuted} space-y-1`}>
                        <p>Food Unit Leader: {entry.food_unit_leader || 'N/A'}</p>
                        <p>Total Personnel: {entry.total_personnel || 'N/A'}</p>
                        <p>Operational Period: {entry.operational_period_from && entry.operational_period_to 
                          ? `${entry.operational_period_from} to ${entry.operational_period_to}` 
                          : 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        onClick={() => {
                          setEditingEntry(entry);
                          setView('form');
                        }}
                        variant="outline"
                        size="sm"
                        className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => handlePrintEntry(entry)}
                        variant="outline"
                        size="sm"
                        className="border-green-500 text-green-400 hover:bg-green-500/10"
                      >
                        <Printer className="h-4 w-4 mr-1" />
                        Print
                      </Button>
                      <Button
                        onClick={() => deleteEntry(entry.id)}
                        variant="outline"
                        size="sm"
                        className="border-red-500 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Form view
  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center print:hidden">
        <Button onClick={() => {
          setView('list');
          setEditingEntry(null);
        }} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to List
        </Button>
        <Button onClick={handlePrint} className="bg-blue-500 hover:bg-blue-600">
          <Printer className="h-4 w-4 mr-2" />
          Print Food Unit
        </Button>
      </div>

      <div className="space-y-6">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>Food Unit</h1>
          <p className={`${theme.colors.textMuted}`}>Logistics Section - Services Branch</p>
        </div>

        {/* Basic Information Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-orange-500">Basic Information</CardTitle>
            <div className="flex items-center gap-2 print:hidden">
              {renderSaveStatus('basic')}
              <Button onClick={() => saveCard('basic')} size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Exercise Name</Label>
                <div className={`p-2 bg-gray-800 rounded border border-gray-600`}>
                  <span className={`${theme.colors.textPrimary}`}>{currentExercise?.exercise_name || 'N/A'}</span>
                </div>
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Incident Name</Label>
                <Input
                  value={formData.incident_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, incident_name: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="Enter incident name"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Operational Period From</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.operational_period_from ? formData.operational_period_from.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.operational_period_from ? formData.operational_period_from.split('T')[1] : '00:00';
                      setFormData(prev => ({ ...prev, operational_period_from: `${date}T${time}` }));
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.operational_period_from ? formData.operational_period_from.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.operational_period_from ? formData.operational_period_from.split('T')[0] : '';
                        setFormData(prev => ({ ...prev, operational_period_from: `${date}T${value}` }));
                      }
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                </div>
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Operational Period To</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.operational_period_to ? formData.operational_period_to.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.operational_period_to ? formData.operational_period_to.split('T')[1] : '00:00';
                      setFormData(prev => ({ ...prev, operational_period_to: `${date}T${time}` }));
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.operational_period_to ? formData.operational_period_to.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.operational_period_to ? formData.operational_period_to.split('T')[0] : '';
                        setFormData(prev => ({ ...prev, operational_period_to: `${date}T${value}` }));
                      }
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Food Unit Leader</Label>
                <Input
                  value={formData.food_unit_leader}
                  onChange={(e) => setFormData(prev => ({ ...prev, food_unit_leader: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="Enter name"
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Duration Estimate</Label>
                <Input
                  value={formData.duration_estimate}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration_estimate: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="e.g., 3 days"
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Last Meal Provided</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.last_meal_provided ? formData.last_meal_provided.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.last_meal_provided ? formData.last_meal_provided.split('T')[1] : '00:00';
                      setFormData(prev => ({ ...prev, last_meal_provided: `${date}T${time}` }));
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.last_meal_provided ? formData.last_meal_provided.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.last_meal_provided ? formData.last_meal_provided.split('T')[0] : '';
                        setFormData(prev => ({ ...prev, last_meal_provided: `${date}T${value}` }));
                      }
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Personnel Count Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Personnel Information</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Total Personnel</Label>
                <Input
                  type="number"
                  value={formData.total_personnel}
                  onChange={(e) => setFormData(prev => ({ ...prev, total_personnel: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="0"
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Incoming Resources</Label>
                <Input
                  type="number"
                  value={formData.incoming_resources}
                  onChange={(e) => setFormData(prev => ({ ...prev, incoming_resources: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="0"
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Outgoing Resources</Label>
                <Input
                  type="number"
                  value={formData.outgoing_resources}
                  onChange={(e) => setFormData(prev => ({ ...prev, outgoing_resources: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="0"
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Expected Next Period</Label>
                <Input
                  type="number"
                  value={formData.expected_next_period}
                  onChange={(e) => setFormData(prev => ({ ...prev, expected_next_period: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="0"
                />
              </div>
            </div>

            {/* Personnel by Location */}
            <div className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <Label className={`${theme.colors.textSecondary}`}>Personnel by Location/Assignment</Label>
                <Button onClick={addPersonnelLocation} variant="outline" size="sm" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Location
                </Button>
              </div>
              
              <div className="space-y-3">
                {formData.personnel_locations.map((loc, index) => (
                  <div key={loc.id} className="grid grid-cols-12 gap-3 items-center">
                    <div className="col-span-4">
                      <Input
                        value={loc.location}
                        onChange={(e) => updatePersonnelLocation(index, 'location', e.target.value)}
                        placeholder="Location"
                        className={`bg-gray-800 border-gray-600 text-white`}
                      />
                    </div>
                    <div className="col-span-4">
                      <Input
                        value={loc.assignment}
                        onChange={(e) => updatePersonnelLocation(index, 'assignment', e.target.value)}
                        placeholder="Assignment/Role"
                        className={`bg-gray-800 border-gray-600 text-white`}
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        type="number"
                        value={loc.count}
                        onChange={(e) => updatePersonnelLocation(index, 'count', e.target.value)}
                        placeholder="Count"
                        className={`bg-gray-800 border-gray-600 text-white`}
                      />
                    </div>
                    <div className="col-span-1">
                      <Button
                        onClick={() => deletePersonnelLocation(index)}
                        variant="outline"
                        size="sm"
                        className="border-red-500 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Meal Planning Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Meal Planning & Schedules</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <Label className={`${theme.colors.textSecondary}`}>Meal Schedules</Label>
              <Button onClick={addMealSchedule} variant="outline" size="sm" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-1" />
                Add Meal
              </Button>
            </div>
            
            <div className="space-y-3 mb-6">
              {formData.meal_schedules.map((meal, index) => (
                <div key={meal.id} className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-2">
                    <select
                      value={meal.meal_type}
                      onChange={(e) => updateMealSchedule(index, 'meal_type', e.target.value)}
                      className={`w-full p-2 rounded bg-gray-800 border-gray-600 text-white`}
                    >
                      <option value="">Type</option>
                      <option value="breakfast">Breakfast</option>
                      <option value="lunch">Lunch</option>
                      <option value="dinner">Dinner</option>
                      <option value="snack">Snack</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="text"
                      placeholder="HH:MM"
                      pattern="[0-2][0-9]:[0-5][0-9]"
                      maxLength="5"
                      value={meal.scheduled_time}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^0-9:]/g, '');
                        if (value.length === 2 && !value.includes(':')) {
                          value = value + ':';
                        }
                        if (value.length <= 5) {
                          updateMealSchedule(index, 'scheduled_time', value);
                        }
                      }}
                      className={`bg-gray-800 border-gray-600 text-white`}
                    />
                  </div>
                  <div className="col-span-5">
                    <Input
                      value={meal.menu}
                      onChange={(e) => updateMealSchedule(index, 'menu', e.target.value)}
                      placeholder="Menu items"
                      className={`bg-gray-800 border-gray-600 text-white`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      value={meal.estimated_count}
                      onChange={(e) => updateMealSchedule(index, 'estimated_count', e.target.value)}
                      placeholder="Est. Count"
                      className={`bg-gray-800 border-gray-600 text-white`}
                    />
                  </div>
                  <div className="col-span-1">
                    <Button
                      onClick={() => deleteMealSchedule(index)}
                      variant="outline"
                      size="sm"
                      className="border-red-500 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Special Dietary Requirements / Allergies</Label>
              <Textarea
                value={formData.special_dietary_requirements}
                onChange={(e) => setFormData(prev => ({ ...prev, special_dietary_requirements: e.target.value }))}
                className={`bg-gray-800 border-gray-600 text-white`}
                placeholder="List any special dietary needs, allergies, or restrictions..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Food Orders/Procurement Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Food Orders & Procurement</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <Label className={`${theme.colors.textSecondary}`}>Food Orders</Label>
              <Button onClick={addFoodOrder} variant="outline" size="sm" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-1" />
                Add Order
              </Button>
            </div>
            
            <div className="space-y-4">
              {formData.food_orders.map((order, index) => (
                <Card key={order.id} className="bg-gray-800/50 border-gray-700">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Vendor/Caterer Name</Label>
                        <Input
                          value={order.vendor_name}
                          onChange={(e) => updateFoodOrder(index, 'vendor_name', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Vendor name"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Contact Info</Label>
                        <Input
                          value={order.contact_info}
                          onChange={(e) => updateFoodOrder(index, 'contact_info', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Phone/email"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-orange-500 text-xs font-semibold">Items Ordered</Label>
                        <Textarea
                          value={order.items_ordered}
                          onChange={(e) => updateFoodOrder(index, 'items_ordered', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="List items ordered..."
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Quantity</Label>
                        <Input
                          value={order.quantity}
                          onChange={(e) => updateFoodOrder(index, 'quantity', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="e.g., 100 meals"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Cost</Label>
                        <Input
                          value={order.cost}
                          onChange={(e) => updateFoodOrder(index, 'cost', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="$0.00"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Order Date</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={order.order_date ? order.order_date.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = order.order_date ? order.order_date.split('T')[1] : '00:00';
                              updateFoodOrder(index, 'order_date', `${date}T${time}`);
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            pattern="[0-2][0-9]:[0-5][0-9]"
                            maxLength="5"
                            value={order.order_date ? order.order_date.split('T')[1] : ''}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9:]/g, '');
                              if (value.length === 2 && !value.includes(':')) {
                                value = value + ':';
                              }
                              if (value.length <= 5) {
                                const date = order.order_date ? order.order_date.split('T')[0] : '';
                                updateFoodOrder(index, 'order_date', `${date}T${value}`);
                              }
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Delivery Time</Label>
                        <Input
                          type="text"
                          placeholder="HH:MM"
                          pattern="[0-2][0-9]:[0-5][0-9]"
                          maxLength="5"
                          value={order.delivery_time}
                          onChange={(e) => {
                            let value = e.target.value.replace(/[^0-9:]/g, '');
                            if (value.length === 2 && !value.includes(':')) {
                              value = value + ':';
                            }
                            if (value.length <= 5) {
                              updateFoodOrder(index, 'delivery_time', value);
                            }
                          }}
                          className={`bg-gray-800 border-gray-600 text-white`}
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <Button
                        onClick={() => deleteFoodOrder(index)}
                        variant="outline"
                        size="sm"
                        className="border-red-500 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete Order
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Meals Served Log Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Meals Served Log</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <Label className={`${theme.colors.textSecondary}`}>Meal Records</Label>
              <Button onClick={addMealServed} variant="outline" size="sm" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-1" />
                Add Record
              </Button>
            </div>
            
            <div className="space-y-3">
              {formData.meals_served.map((meal, index) => (
                <Card key={meal.id} className="bg-gray-800/50 border-gray-700">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-12 gap-3">
                      <div className="col-span-2">
                        <Label className="text-orange-500 text-xs font-semibold">Meal Type</Label>
                        <select
                          value={meal.meal_type}
                          onChange={(e) => updateMealServed(index, 'meal_type', e.target.value)}
                          className={`w-full p-2 rounded bg-gray-800 border-gray-600 text-white`}
                        >
                          <option value="">Select</option>
                          <option value="breakfast">Breakfast</option>
                          <option value="lunch">Lunch</option>
                          <option value="dinner">Dinner</option>
                          <option value="snack">Snack</option>
                        </select>
                      </div>
                      <div className="col-span-3">
                        <Label className="text-orange-500 text-xs font-semibold">Date/Time Served</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={meal.date_time_served ? meal.date_time_served.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = meal.date_time_served ? meal.date_time_served.split('T')[1] : '00:00';
                              updateMealServed(index, 'date_time_served', `${date}T${time}`);
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            pattern="[0-2][0-9]:[0-5][0-9]"
                            maxLength="5"
                            value={meal.date_time_served ? meal.date_time_served.split('T')[1] : ''}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9:]/g, '');
                              if (value.length === 2 && !value.includes(':')) {
                                value = value + ':';
                              }
                              if (value.length <= 5) {
                                const date = meal.date_time_served ? meal.date_time_served.split('T')[0] : '';
                                updateMealServed(index, 'date_time_served', `${date}T${value}`);
                              }
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                        </div>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-orange-500 text-xs font-semibold">Meals Count</Label>
                        <Input
                          type="number"
                          value={meal.meals_count}
                          onChange={(e) => updateMealServed(index, 'meals_count', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="0"
                        />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-orange-500 text-xs font-semibold">Location</Label>
                        <select
                          value={meal.location}
                          onChange={(e) => updateMealServed(index, 'location', e.target.value)}
                          className={`w-full p-2 rounded bg-gray-800 border-gray-600 text-white`}
                        >
                          <option value="">Select Location</option>
                          {locations.map((loc) => (
                            <option key={loc.id} value={loc.name}>
                              {loc.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-orange-500 text-xs font-semibold mb-1">Actions</Label>
                        <Button
                          onClick={() => deleteMealServed(index)}
                          variant="outline"
                          size="sm"
                          className="w-full border-red-500 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="col-span-12">
                        <Label className="text-orange-500 text-xs font-semibold">Notes</Label>
                        <Input
                          value={meal.notes}
                          onChange={(e) => updateMealServed(index, 'notes', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Additional notes..."
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Water & Beverages Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Water & Beverages</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <Label className={`${theme.colors.textSecondary}`}>Water & Beverage Records</Label>
              <Button onClick={addWaterBeverageRecord} variant="outline" size="sm" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-1" />
                Add Record
              </Button>
            </div>
            
            <div className="space-y-4">
              {formData.water_beverage_records.map((record, index) => (
                <Card key={record.id} className="bg-gray-800/50 border-gray-700">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Water Quantity</Label>
                        <Input
                          value={record.water_quantity}
                          onChange={(e) => updateWaterBeverageRecord(index, 'water_quantity', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="e.g., 500 liters, 100 gallons"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Beverage Type</Label>
                        <Input
                          value={record.beverage_type}
                          onChange={(e) => updateWaterBeverageRecord(index, 'beverage_type', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Coffee, juice, sports drinks, etc."
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Quantity</Label>
                        <Input
                          value={record.quantity}
                          onChange={(e) => updateWaterBeverageRecord(index, 'quantity', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="e.g., 50 units"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Cost</Label>
                        <Input
                          value={record.cost}
                          onChange={(e) => updateWaterBeverageRecord(index, 'cost', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="$0.00"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Distribution Point</Label>
                        <Input
                          value={record.distribution_point}
                          onChange={(e) => updateWaterBeverageRecord(index, 'distribution_point', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Location"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Delivery Date/Time</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={record.delivery_date ? record.delivery_date.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = record.delivery_date ? record.delivery_date.split('T')[1] : '00:00';
                              updateWaterBeverageRecord(index, 'delivery_date', `${date}T${time}`);
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            pattern="[0-2][0-9]:[0-5][0-9]"
                            maxLength="5"
                            value={record.delivery_date ? record.delivery_date.split('T')[1] : ''}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9:]/g, '');
                              if (value.length === 2 && !value.includes(':')) {
                                value = value + ':';
                              }
                              if (value.length <= 5) {
                                const date = record.delivery_date ? record.delivery_date.split('T')[0] : '';
                                updateWaterBeverageRecord(index, 'delivery_date', `${date}T${value}`);
                              }
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-orange-500 text-xs font-semibold">Notes</Label>
                        <Input
                          value={record.notes}
                          onChange={(e) => updateWaterBeverageRecord(index, 'notes', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Additional notes"
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <Button
                        onClick={() => deleteWaterBeverageRecord(index)}
                        variant="outline"
                        size="sm"
                        className="border-red-500 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete Record
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Facility & Safety Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Facility & Safety Information</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <Label className={`${theme.colors.textSecondary}`}>Facility Records</Label>
              <Button onClick={addFacilityRecord} variant="outline" size="sm" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-1" />
                Add Facility
              </Button>
            </div>
            
            <div className="space-y-4">
              {formData.facility_records.map((record, index) => (
                <Card key={record.id} className="bg-gray-800/50 border-gray-700">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Facility Name</Label>
                        <Input
                          value={record.facility_name}
                          onChange={(e) => updateFacilityRecord(index, 'facility_name', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Facility name"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Location/Address</Label>
                        <Input
                          value={record.location}
                          onChange={(e) => updateFacilityRecord(index, 'location', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Facility address/location"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Meal Preparation Area</Label>
                        <Input
                          value={record.meal_prep_area}
                          onChange={(e) => updateFacilityRecord(index, 'meal_prep_area', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Prep area description"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Status</Label>
                        <select
                          value={record.status}
                          onChange={(e) => updateFacilityRecord(index, 'status', e.target.value)}
                          className={`w-full p-2 rounded bg-gray-800 border-gray-600 text-white`}
                        >
                          <option value="">Select Status</option>
                          <option value="active">Active</option>
                          <option value="pending">Pending Inspection</option>
                          <option value="approved">Approved</option>
                          <option value="needs_repair">Needs Repair</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-orange-500 text-xs font-semibold">Equipment Available</Label>
                        <Textarea
                          value={record.equipment_available}
                          onChange={(e) => updateFacilityRecord(index, 'equipment_available', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="List cooking equipment, appliances, etc."
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Inspection Date/Time</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={record.inspection_date ? record.inspection_date.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = record.inspection_date ? record.inspection_date.split('T')[1] : '00:00';
                              updateFacilityRecord(index, 'inspection_date', `${date}T${time}`);
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            pattern="[0-2][0-9]:[0-5][0-9]"
                            maxLength="5"
                            value={record.inspection_date ? record.inspection_date.split('T')[1] : ''}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9:]/g, '');
                              if (value.length === 2 && !value.includes(':')) {
                                value = value + ':';
                              }
                              if (value.length <= 5) {
                                const date = record.inspection_date ? record.inspection_date.split('T')[0] : '';
                                updateFacilityRecord(index, 'inspection_date', `${date}T${value}`);
                              }
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Inspector Name</Label>
                        <Input
                          value={record.inspector_name}
                          onChange={(e) => updateFacilityRecord(index, 'inspector_name', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Inspector name"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-orange-500 text-xs font-semibold">Health & Safety Notes</Label>
                        <Textarea
                          value={record.health_safety_notes}
                          onChange={(e) => updateFacilityRecord(index, 'health_safety_notes', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Safety measures, compliance notes, issues identified, etc."
                          rows={3}
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <Button
                        onClick={() => deleteFacilityRecord(index)}
                        variant="outline"
                        size="sm"
                        className="border-red-500 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete Facility
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Cost Tracking Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Cost Tracking</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Total Estimated Cost (Auto-calculated)</Label>
                <div className={`p-2 bg-gray-800 rounded border border-gray-600 bg-gray-800/50`}>
                  <span className={`${theme.colors.textPrimary} font-semibold text-lg text-green-400`}>
                    {formData.total_estimated_cost || '$0.00'}
                  </span>
                  <span className={`${theme.colors.textMuted} text-sm ml-2`}>
                    (Sum of all food orders and water/beverage costs)
                  </span>
                </div>
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Budget Status</Label>
                <Input
                  value={formData.budget_status}
                  onChange={(e) => setFormData(prev => ({ ...prev, budget_status: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="On track, over budget, etc."
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Prepared By Section */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-orange-500">Prepared By</CardTitle>
            <div className="flex items-center gap-2 print:hidden">
              {renderSaveStatus('prepared')}
              <Button onClick={() => saveCard('prepared')} disabled={loading} className="bg-green-600 hover:bg-green-700" size="sm">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Prepared By</Label>
                <Input
                  value={formData.prepared_by}
                  onChange={(e) => setFormData(prev => ({ ...prev, prepared_by: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="Name"
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Signature</Label>
                <Input
                  value={formData.signature}
                  onChange={(e) => setFormData(prev => ({ ...prev, signature: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="Digital signature"
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Date/Time Prepared</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.date_time_prepared ? formData.date_time_prepared.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.date_time_prepared ? formData.date_time_prepared.split('T')[1] : '00:00';
                      setFormData(prev => ({ ...prev, date_time_prepared: `${date}T${time}` }));
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.date_time_prepared ? formData.date_time_prepared.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.date_time_prepared ? formData.date_time_prepared.split('T')[0] : '';
                        setFormData(prev => ({ ...prev, date_time_prepared: `${date}T${value}` }));
                      }
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end print:hidden">
          <Button onClick={saveEntry} disabled={loading} className="bg-orange-500 hover:bg-orange-600">
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : (editingEntry ? 'Update' : 'Save')} Food Unit
          </Button>
        </div>
      </div>
    </div>
  );
};


// Facilities Unit Form

export default FoodUnitForm;
